import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRing, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ROLE = { super_admin: 'Super Admin', gestor: 'Gestor', admin: 'Admin', parceiro: 'Parceiro', backoffice: 'Backoffice', comercial: 'Comercial', chefe_equipa: 'Chefe de Equipa' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' }) : '');

export default function PushStatusPage() {
  const { user } = useAuth();
  const allowed = ['super_admin', 'gestor', 'admin'].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [filtro, setFiltro] = useState('todos');
  useEffect(() => { if (allowed) axios.get(`${API_URL}/push/status`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [allowed]);

  const test = async (u) => {
    try {
      const { data } = await axios.post(`${API_URL}/push/test/${u.id}`);
      data.sent ? toast.success(`Teste enviado para ${u.name} (${data.sent} dispositivo(s))`) : toast.error(`${u.name} não tem dispositivos ativos`);
    } catch { toast.error('Erro ao enviar teste'); }
  };
  const [mailing, setMailing] = useState(false);
  const remind = async () => {
    if (!window.confirm('Enviar email com os passos a todos os comerciais que ainda não ativaram os avisos?')) return;
    setMailing(true);
    try { const { data } = await axios.post(`${API_URL}/push/remind-email`); toast.success(`${data.queued} email(s) a enviar`); } catch { toast.error('Erro ao enviar emails'); } finally { setMailing(false); }
  };
  const list = (rows || []).filter((u) => (filtro === 'todos' || (filtro === 'ativos' ? u.devices > 0 : u.devices === 0))
    && (!q || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase())));
  const ativos = (rows || []).filter((u) => u.devices > 0).length;

  if (!allowed) return <Layout><p className="p-6 text-sm text-slate-500" data-testid="push-status-forbidden">Sem permissão para ver esta página.</p></Layout>;
  return (
    <Layout>
    <div className="space-y-6" data-testid="push-status-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-[Outfit] text-[#2D3E6F] flex items-center gap-2"><BellRing className="w-7 h-7" />Avisos no telefone</h1>
        <p className="text-sm text-slate-500 mt-1">Quem já ativou as notificações no telefone e quem falta.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {[['todos', `Todos (${(rows || []).length})`], ['ativos', `Ativos (${ativos})`], ['falta', `Falta ativar (${(rows || []).length - ativos})`]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFiltro(k)} data-testid={`push-filter-${k}`}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${filtro === k ? 'bg-[#2D3E6F] text-white border-[#2D3E6F]' : 'bg-white border-slate-200 text-slate-600'}`}>{l}</button>
        ))}
        <Button size="sm" disabled={mailing} onClick={remind} className="bg-[#2D3E6F] hover:bg-[#24335c]" data-testid="push-remind-email-btn">{mailing ? 'A enviar…' : 'Enviar email a quem falta ativar'}</Button>
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar" className="pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="push-search" />
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
        {!rows && <p className="p-6 text-sm text-slate-400">A carregar…</p>}
        {rows && !list.length && <p className="p-6 text-sm text-slate-400">Sem utilizadores.</p>}
        {list.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`push-user-${u.id}`}>
            <div className="flex-1 min-w-[180px]">
              <p className="font-medium text-slate-800">{u.name}</p>
              <p className="text-xs text-slate-500">{ROLE[u.role] || u.role} · {u.email}</p>
            </div>
            {u.devices > 0 ? (
              <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2.5 py-1" data-testid={`push-status-${u.id}`}>
                Ativo · {u.devices} disp.{u.plataformas?.length ? ` (${u.plataformas.join(', ')})` : ''} · {fmt(u.last)}
              </span>
            ) : (
              <span className="text-xs font-semibold text-rose-700 bg-rose-100 rounded-full px-2.5 py-1" data-testid={`push-status-${u.id}`}>Falta ativar</span>
            )}
            <Button size="sm" variant="outline" disabled={!u.devices} onClick={() => test(u)} data-testid={`push-test-${u.id}`}>Enviar teste</Button>
          </div>
        ))}
      </div>
    </div>
    </Layout>
  );
}
